/**
 * The similar-players list's row renderers.
 *
 * Extracted from `SimilarPlayersTabContainer`, which went over the 600-line cap
 * when the View's rendering work moved up (SE-8019). These five belong together
 * — a row press navigates, a row draws, a key identifies it, a footer closes
 * the page, and the list ties them to the data — so they move as a unit rather
 * than being scattered to satisfy a line count.
 * @module features/player-detail/hooks/useSimilarPlayersListRenderers
 */

import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { Href, usePathname, useRouter } from "expo-router";
import React, { useCallback, useMemo } from "react";

import { Box } from "@/components/atoms/Box";
import { Spinner } from "@/components/atoms/Spinner";
import SimilarPlayerRow from "@/features/player-detail/components/organisms/SimilarPlayerRow";
import {
  getBadgeConfigForSortField,
  getBadgeTypeForSortField,
  getScoreForSortField,
} from "@/features/player-detail/utils/similarPlayersUtils";
import { formatCountrySegment } from "@/features/team-selection/utils/countryFlag";
import { useNavigationContext } from "@/providers/NavigationProvider";

import type {
  SimilarPlayerSortField,
  SimilarPlayersPlayerResultFragment,
} from "@/generated/graphql";

/**
 * FlashList content padding, hoisted so the shared renderer keeps the mobile
 * (4px) and desktop (8px) values it had before SE-7580 unified the two call
 * sites into one renderer.
 */
const MOBILE_LIST_CONTENT_STYLE = { paddingVertical: 4 };

const DESKTOP_LIST_CONTENT_STYLE = { paddingVertical: 8 };

/** What the renderers need from the tab. */
export interface SimilarPlayersListRenderersArgs {
  /** Whether the mobile branch is active. */
  readonly isMobile: boolean;
  /** Whether the dark theme is active. */
  readonly isDark: boolean;
  /** The sort field the CURRENT results were fetched under. */
  readonly committedSortField: SimilarPlayerSortField;
  /** Whether a refresh is in flight. */
  readonly refreshing: boolean;
  /** Opens the add-to-watchlist sheet for a row. */
  readonly onAddPress: (item: SimilarPlayersPlayerResultFragment) => void;
  /** Records the row press before navigation. */
  readonly onRowPress: (playerId: string) => void;
  /** Retries the read. */
  readonly onRefresh: () => void;
  /** Fetches the next page. */
  readonly onLoadMore: () => void;
}

/**
 * Builds the list's renderers.
 * @param args - What the renderers need from the tab.
 * @param args.isMobile - Whether the mobile branch is active.
 * @param args.isDark - Whether the dark theme is active.
 * @param args.committedSortField - The sort field the results were fetched under.
 * @param args.refreshing - Whether a refresh is in flight.
 * @param args.onAddPress - Opens the add-to-watchlist sheet.
 * @param args.onRowPress - Records the row press before navigation.
 * @param args.onRefresh - Retries the read.
 * @param args.onLoadMore - Fetches the next page.
 * @returns The list renderer.
 */
export const useSimilarPlayersListRenderers = ({
  isMobile,
  isDark,
  committedSortField,
  refreshing,
  onAddPress,
  onRowPress,
  onRefresh,
  onLoadMore,
}: SimilarPlayersListRenderersArgs) => {
  const router = useRouter();
  const pathname = usePathname();
  const { setNavigationSource, pushBreadcrumb } = useNavigationContext();

  const playersBasePath = useMemo(() => {
    const playersIdx = pathname.lastIndexOf("/players/");
    return playersIdx >= 0
      ? pathname.substring(0, playersIdx + "/players".length)
      : "/players";
  }, [pathname]);

  const handleRowNavigate = useCallback(
    (playerId: string, playerName?: string) => {
      onRowPress(playerId);
      setNavigationSource("similar-players");
      const playerPath = `${playersBasePath}/${playerId}`;
      pushBreadcrumb({
        label: playerName || "Player",
        path: playerPath,
      });
      router.push(playerPath as Href);
    },
    [onRowPress, router, setNavigationSource, playersBasePath, pushBreadcrumb]
  );

  const badgeConfig = getBadgeConfigForSortField(committedSortField);
  const badgeType = getBadgeTypeForSortField(committedSortField);

  const renderItem: ListRenderItem<SimilarPlayersPlayerResultFragment> =
    useCallback(
      ({ item }) => {
        const player = item.player;
        if (!player) return null;

        const positionText =
          player.positions
            ?.map(p => p?.abbreviation ?? "")
            .filter(Boolean)
            .join(", ") ?? "";
        const clubName = player.team?.name ?? "";
        const nationalitySegment =
          formatCountrySegment(player.nationality?.name) ?? "";
        const subtitleText = [positionText, clubName, nationalitySegment]
          .filter(Boolean)
          .join(" · ");
        const playerName = player?.name ?? "";
        const isOnWatchlist = player.isWatchedByCurrentUser ?? false;
        const score = getScoreForSortField(item, committedSortField);

        const row = (
          <SimilarPlayerRow
            isMobile={isMobile}
            item={item}
            playerId={player.id}
            playerName={playerName}
            subtitleText={subtitleText}
            score={score}
            badgeType={badgeType}
            badgeMin={badgeConfig.min}
            badgeMax={badgeConfig.max}
            badgeIsHigherBetter={badgeConfig.isHigherBetter}
            badgeIsDefaultStyle={badgeConfig.isDefaultStyle}
            isOnWatchlist={isOnWatchlist}
            onAddPress={onAddPress}
            onRowPress={handleRowNavigate}
          />
        );

        // Mobile rows are bordered cards, so without space between them the
        // borders abut and the list reads as one fused block (SE-6991). The
        // list supplies that space on its own per-row container rather than
        // the card carrying an offset. Desktop rows are borderless inside a
        // single bordered container and never had this problem, so they are
        // returned unwrapped.
        return isMobile ? <Box className="pb-3">{row}</Box> : row;
      },
      [
        isMobile,
        onAddPress,
        handleRowNavigate,
        committedSortField,
        badgeConfig,
        badgeType,
      ]
    );

  const keyExtractor = useCallback(
    (item: SimilarPlayersPlayerResultFragment) => item.player?.id ?? "",
    []
  );

  const renderFooterComponent = useCallback(
    () =>
      refreshing ? (
        <Box className="items-center py-4">
          <Spinner size="small" color={isDark ? "#60A5FA" : "#3B82F6"} />
        </Box>
      ) : null,
    [refreshing, isDark]
  );

  /**
   * Renders the player rows. Only ever called for the `ready` and `stale`
   * states, i.e. when there is at least one row — so `ListEmptyComponent`
   * is deliberately absent. That is what makes AC3 structural rather than a
   * condition someone has to remember: the "no similar players" copy no
   * longer lives inside the list, so it cannot render alongside the
   * refreshing footer spinner (the reported screenshot).
   */
  const renderPlayerList = useCallback(
    (players: readonly SimilarPlayersPlayerResultFragment[]) => (
      <FlashList
        data={players as SimilarPlayersPlayerResultFragment[]}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        onRefresh={onRefresh}
        refreshing={refreshing}
        ListFooterComponent={renderFooterComponent}
        contentContainerStyle={
          isMobile ? MOBILE_LIST_CONTENT_STYLE : DESKTOP_LIST_CONTENT_STYLE
        }
      />
    ),
    [
      renderItem,
      keyExtractor,
      onLoadMore,
      onRefresh,
      refreshing,
      renderFooterComponent,
      isMobile,
    ]
  );

  /**
   * One rendering per resolution state. The `never` fallthrough makes a
   * newly-added state a COMPILE error here rather than a silent fall-back
   * to the empty copy — the SE-6491 pattern.
   */

  return { renderPlayerList };
};
